// Toasts (DESIGN_SYSTEM §9.2): polite live region; 5 s, or 8 s with Undo; errors don't auto-dismiss;
// at most 3 stacked; bottom-centre above the bottom nav on mobile, bottom-right on desktop. A toast is
// never the only place a money result appears.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import type { Tone } from "../state";
import { toneClasses } from "../tones";
import { Icon, type IconName } from "./Icon";

export type ToastTone = "info" | "success" | "warning" | "error";

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  body?: string;
  action?: { label: string; onClick: () => void };
  /** Overrides the default duration (ms). Errors ignore it and stay until dismissed. */
  duration?: number;
}
interface ToastItem extends ToastInput {
  id: number;
}

interface ToastApi {
  show: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

const toneOf: Record<ToastTone, { tone: Tone; icon: IconName }> = {
  info: { tone: "action", icon: "info" },
  success: { tone: "success", icon: "check" },
  warning: { tone: "warning", icon: "triangle" },
  error: { tone: "danger", icon: "octagon" },
};
const MAX = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback((toast: ToastInput) => {
    const id = nextId.current++;
    setItems((xs) => [...xs, { ...toast, id }].slice(-MAX));
    return id;
  }, []);
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  const polite = items.filter((i) => i.tone !== "error");
  const assertive = items.filter((i) => i.tone === "error");
  return (
    <ToastContext.Provider value={api}>
      {children}
      <section className="ady-toasts" aria-label={t("toast.regionLabel")}>
        <div role="status" aria-live="polite" className="contents">
          {polite.map((item) => (
            <ToastView key={item.id} item={item} onDismiss={dismiss} />
          ))}
        </div>
        <div role="alert" aria-live="assertive" className="contents">
          {assertive.map((item) => (
            <ToastView key={item.id} item={item} onDismiss={dismiss} />
          ))}
        </div>
      </section>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const tone = item.tone ?? "info";
  useEffect(() => {
    if (tone === "error") return;
    const ms = item.duration ?? (item.action ? 8000 : 5000);
    const id = setTimeout(() => onDismiss(item.id), ms);
    return () => clearTimeout(id);
  }, [item, tone, onDismiss]);
  const { tone: t2, icon } = toneOf[tone];
  return (
    <div
      className="flex w-full max-w-md items-start gap-3 rounded-md border border-subtle bg-overlay p-3 text-fg shadow-2"
      data-testid="toast"
      data-tone={tone}
    >
      <Icon name={icon} className={cx("mt-0.5 flex-none", toneClasses[t2].fg)} />
      <div className="flex-1">
        <p className="text-md font-medium">{item.title}</p>
        {item.body ? <p className="text-sm text-fg-secondary">{item.body}</p> : null}
      </div>
      {item.action ? (
        <button
          type="button"
          className="min-h-11 rounded-sm px-3 text-md font-semibold text-link hover:bg-action-bg"
          onClick={() => {
            item.action?.onClick();
            onDismiss(item.id);
          }}
        >
          {item.action.label}
        </button>
      ) : null}
      <button
        type="button"
        className="flex size-11 flex-none items-center justify-center rounded-sm text-fg-secondary hover:bg-hover"
        aria-label={t("toast.dismissLabel")}
        onClick={() => onDismiss(item.id)}
      >
        <Icon name="x" size={18} />
      </button>
    </div>
  );
}
